import { Link } from "react-router-dom";
import { useEffect } from "react";
import { QrCode, MagnifyingGlass, ArrowRight, ShieldCheck, Lightning, Globe, EnvelopeSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { getVisitorId } from "@/lib/visitor";

export default function Landing() {
  useEffect(() => {
    api.post("/track", { path: "/", visitor_id: getVisitorId(), referrer: document.referrer || "" }).catch(() => {});
  }, []);
  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {/* Top nav */}
      <header style={{ borderBottom: "1px solid var(--border)", background: "#fff" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" data-testid="landing-logo" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "#000" }}>
            <div style={{ width: 28, height: 28, background: "#000", display: "grid", placeItems: "center" }}>
              <QrCode size={18} color="#fff" weight="bold" />
            </div>
            <span className="font-display" style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.02em" }}>7AuraDigital</span>
          </Link>
          <nav style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <a href="#contact" className="btn btn-ghost" data-testid="landing-contact-link">
              Contact
            </a>
            <Link to="/search" className="btn btn-ghost" data-testid="landing-search-link">
              <MagnifyingGlass size={16} /> Search
            </Link>
            <Link to="/login" className="btn btn-primary" data-testid="landing-login-link">
              Admin Login <ArrowRight size={14} />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 24px 60px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 40 }}>
          <div>
            <div className="overline" style={{ marginBottom: 24 }}>QR Information Management Platform</div>
            <h1 className="font-display" style={{ fontSize: "clamp(40px, 7vw, 96px)", fontWeight: 900, lineHeight: 0.95, letterSpacing: "-0.04em", margin: 0 }}>
              One scan.<br />
              Everything <span style={{ color: "var(--accent)" }}>they need to know</span>.
            </h1>
            <p style={{ fontSize: 20, color: "var(--text-2)", maxWidth: 720, marginTop: 24, lineHeight: 1.6 }}>
              Create rich, mobile-first information pages. Generate dynamic QR codes that
              link to them. Edit destinations anytime—no reprints required.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 32, flexWrap: "wrap" }}>
              <Link to="/login" className="btn btn-primary" data-testid="hero-cta-login" style={{ padding: "14px 22px", fontSize: 15 }}>
                Open Admin Console <ArrowRight size={16} />
              </Link>
              <Link to="/search" className="btn" data-testid="hero-cta-search" style={{ padding: "14px 22px", fontSize: 15 }}>
                <MagnifyingGlass size={16} /> Browse Public Pages
              </Link>
            </div>
          </div>

          <div style={{ marginTop: 40, border: "1px solid var(--border)", overflow: "hidden" }}>
            <img alt="QR scan" src="https://images.unsplash.com/photo-1595079676339-1534801ad6cf?w=1600&q=80"
              style={{ width: "100%", height: 480, objectFit: "cover", display: "block" }} />
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: "var(--surface)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "60px 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 0 }}>
          {[
            { icon: <Lightning size={28} weight="duotone" />, title: "Dynamic QR codes", body: "Generate unlimited QR codes. Update destinations without reprinting." },
            { icon: <Globe size={28} weight="duotone" />, title: "Rich CMS", body: "Featured images, galleries, PDFs, video, maps, FAQ — all mobile-first." },
            { icon: <ShieldCheck size={28} weight="duotone" />, title: "Privacy-first analytics", body: "Track scans, devices and top pages without 3rd-party trackers." },
          ].map((f, i) => (
            <div key={i} data-testid={`feature-card-${i}`} style={{ padding: 32, borderRight: i < 2 ? "1px solid var(--border)" : "none" }}>
              <div style={{ color: "var(--accent)" }}>{f.icon}</div>
              <h3 className="font-display" style={{ fontSize: 22, fontWeight: 800, marginTop: 16, marginBottom: 8 }}>{f.title}</h3>
              <p style={{ color: "var(--text-2)", margin: 0 }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 24px" }} data-testid="landing-contact">
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 40, alignItems: "center" }}>
          <div>
            <div className="overline" style={{ marginBottom: 16 }}>Contact us</div>
            <h2 className="font-display" style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 900, lineHeight: 1, letterSpacing: "-0.03em", margin: 0 }}>
              Let's build something<br />worth <span style={{ color: "var(--accent)" }}>scanning</span>.
            </h2>
            <p style={{ fontSize: 18, color: "var(--text-2)", marginTop: 20, maxWidth: 640, lineHeight: 1.6 }}>
              Questions, partnerships, or bulk QR requirements? Drop us a note.
            </p>
            <a href="mailto:7auradigital@gmail.com"
               data-testid="landing-contact-email"
               style={{ display: "inline-flex", alignItems: "center", gap: 12, marginTop: 24, padding: "16px 22px",
                        background: "#000", color: "#fff", textDecoration: "none", fontWeight: 600, fontSize: 15, borderRadius: 2 }}>
              <EnvelopeSimple size={18} weight="bold" /> 7auradigital@gmail.com
            </a>
          </div>
        </div>
      </section>

      <footer style={{ padding: "32px 24px", color: "var(--text-2)", fontSize: 13, textAlign: "center", borderTop: "1px solid var(--border)" }}>
        © 7AuraDigital · Built on open standards
      </footer>
    </div>
  );
}
